/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useState } from "react";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DomainIcon from "@mui/icons-material/Domain";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import { FIELD_GROUP_ORDER, LedgerField } from "./helpers/normalize";
import { ValueOrEmpty } from "./LedgerRow";
import { AdHocEditControls, isAdHocEditable } from "./AdHocEdit";
import {
  CompanyResolution,
  CompanyResolutionActions,
  isAccountResolvedField,
} from "./CompanyResolution";
import { useFocusReturn } from "./helpers/useFocusReturn";
import classes from "./css/recordReview.module.css";

/*
 * The rest of the record - always visible, grouped semantically: who the
 * person is, where they work and how to reach them, then where they are.
 * Every field appears here (pending ones too, with their incumbent value),
 * so the layout is identical across records.
 *
 * Which section a field belongs to is part of its roster entry in normalize,
 * so this component owns no field list of its own - it only reads the group
 * each row arrives with.
 *
 * Each editable row carries the ad-hoc overwrite pencil from the modal: a
 * confirmed value goes straight to the CRM through the same endpoints the
 * modal uses.
 */
export interface FullRecordActions {
  disabled: boolean;
  /** Absent when ad-hoc text writes are unavailable (no explain payload). */
  onAdHocApprove?: (field: LedgerField, newValue: string) => void;
  /**
   * Company is edited through the account picker instead of a text box, so it
   * carries its own pencil independent of the one above.
   */
  companyResolution?: CompanyResolutionActions;
  /**
   * The system the record lives in, recorded on any email verification an
   * edited address asks for. Optional: a verdict is the address's.
   */
  integratedSystemId?: number | null;
  /** The record's CRM contact id, linked to any email verification an edit asks for. */
  crmContactId?: string | null;
}

/**
 * A field whose stored value the CRM has moved past since Cirrom last read it.
 * The note rides the row rather than replacing it: the stored value is still
 * what Cirrom analyses, so the surface says what differs and why.
 */
export interface UnsyncedFieldNote {
  /** What the CRM holds now, shown in place of the stored value when known. */
  value?: string;
  label: string;
  title: string;
}

/*
 * Neutral chrome, like the source chips: this is a fact about timing, not a
 * health signal, so it takes no ramp colour and no amber.
 */
const UnsyncedChip = (props: { note: UnsyncedFieldNote }) => (
  <Tooltip title={props.note.title}>
    <Chip
      label={props.note.label}
      size="small"
      variant="outlined"
      sx={{
        color: "text.secondary",
        borderColor: "divider",
        height: "20px",
        flexShrink: 0,
        "& .MuiChip-label": { fontSize: "13px" },
      }}
    />
  </Tooltip>
);

export const FullRecordSection = (props: {
  fields: LedgerField[];
  actions?: FullRecordActions;
  /** Rows the CRM has changed since the last sync, keyed by field name. */
  unsynced?: Record<string, UnsyncedFieldNote>;
  /**
   * The same, for a whole group at once - keyed by group label. Every
   * address line read from an old account is stale for the same reason, and
   * saying it once beats a chip on each line.
   */
  unsyncedGroups?: Record<string, UnsyncedFieldNote>;
  /**
   * The CRM these fields belong to, named so the read-only state can say where
   * they ARE edited. Neutral wording when the vendor is unrecognised.
   */
  crmLabel?: string;
}) => {
  const { actions } = props;
  const [editingField, setEditingField] = useState<string | null>(null);
  const focus = useFocusReturn();
  // Every way out of an editor puts focus back on the pencil that opened it.
  const closeEditor = (fieldName: string) => {
    setEditingField(null);
    focus.returnTo(fieldName);
  };

  if (props.fields.length === 0) {
    return null;
  }

  const groups = FIELD_GROUP_ORDER.map((label) => ({
    label: label as string,
    fields: props.fields.filter((field) => field.group === label),
  })).filter((group) => group.fields.length > 0);

  // A field outside every group still deserves a home rather than silence.
  const ungrouped = props.fields.filter((field) => !field.group);
  if (ungrouped.length > 0) {
    groups.push({ label: "Other", fields: ungrouped });
  }

  /*
   * Two pencils, one affordance. A company cannot be overwritten with text -
   * what the CRM holds is a link to an account - so its pencil opens the
   * account picker the ledger uses, and every other field keeps the text box.
   */
  const editsThroughAccount = (field: LedgerField) =>
    isAccountResolvedField(field.fieldName) &&
    Boolean(actions?.companyResolution);
  const canEdit = (field: LedgerField) =>
    editsThroughAccount(field) ||
    (Boolean(actions?.onAdHocApprove) && isAdHocEditable(field.fieldName));

  const renderRow = (field: LedgerField) => {
    const unsynced = props.unsynced?.[field.fieldName];
    if (editingField === field.fieldName && actions) {
      /*
       * The picker needs the column's full width - a label beside it would
       * leave the search box narrower than the account names it lists - so
       * the row stacks while it is open.
       */
      return (
        <div
          key={field.fieldName}
          className={
            editsThroughAccount(field)
              ? classes.fullRecordEditRow
              : classes.fullRecordRow
          }
        >
          <div className={classes.fullRecordLabel}>
            <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
              {field.label}
            </Typography>
          </div>
          {editsThroughAccount(field) ? (
            <CompanyResolution
              field={field}
              disabled={actions.disabled}
              actions={actions.companyResolution!}
              onClose={() => closeEditor(field.fieldName)}
            />
          ) : (
            <AdHocEditControls
              label={field.label}
              initialValue={field.crmValue ?? ""}
              disabled={actions.disabled}
              fieldName={field.fieldName}
              verification={{
                crmContactId: actions.crmContactId,
                integratedSystemId: actions.integratedSystemId,
              }}
              onSubmit={(value) => {
                actions.onAdHocApprove?.(field, value);
                closeEditor(field.fieldName);
              }}
              onCancel={() => closeEditor(field.fieldName)}
            />
          )}
        </div>
      );
    }

    return (
      <div key={field.fieldName} className={classes.fullRecordRow}>
        <div className={classes.fullRecordLabel}>
          {field.fromAccount && (
            <Tooltip title="Field linked from account">
              <DomainIcon
                className={classes.fieldLabelIcon}
                sx={{ color: "text.secondary" }}
              />
            </Tooltip>
          )}
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {field.label}
          </Typography>
        </div>
        <ValueOrEmpty
          value={unsynced?.value ?? field.crmValue}
          isTextCompany={!unsynced?.value && field.crmValueIsTextCompany}
          unknown={field.valueUnknown}
        />
        {unsynced && <UnsyncedChip note={unsynced} />}
        {actions && canEdit(field) && (
          <Tooltip
            title={
              editsThroughAccount(field)
                ? `Choose a different account for ${field.label}`
                : `Enter a new value for ${field.label}`
            }
          >
            <span className={classes.fieldEditSlot}>
              <IconButton
                size="small"
                className={classes.fieldEditButton}
                disabled={actions.disabled}
                aria-label={`Edit ${field.label}`}
                ref={focus.register(field.fieldName)}
                /*
                 * Quieter than the ledger's own controls on purpose: 19 of
                 * these sit below the decision area, and at equal weight they
                 * pulled the eye away from the changes actually waiting on a
                 * human. Still always visible, just no longer competing.
                 */
                sx={{
                  color: "text.disabled",
                  "&:hover": { color: "text.secondary" },
                }}
                onClick={() => setEditingField(field.fieldName)}
              >
                <DriveFileRenameOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
    );
  };

  return (
    <div className={classes.fullRecord}>
      <Typography
        variant="body2"
        component="h3"
        sx={{ color: "text.secondary", fontWeight: 600 }}
      >
        Full record · {props.fields.length} field
        {props.fields.length === 1 ? "" : "s"}
      </Typography>
      {/*
       * With no actions there are no pencils, and the absence read as a bug:
       * an account with no anchoring ticket has no ad-hoc write path, and the
       * surface said nothing about it. So the rule is stated - where the
       * fields are edited, and that Cirrom's approvals land in the same place
       * - rather than left for the steward to infer from missing icons.
       */}
      {!actions && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`Fields are edited in ${
            props.crmLabel ?? "your CRM"
          }. Cirrom writes approved changes there.`}
        </Typography>
      )}
      <div className={classes.fullRecordGroups}>
        {groups.map((group) => (
          <div key={group.label} className={classes.fullRecordGroup}>
            <div className={classes.fullRecordGroupHeading}>
              <Typography
                variant="body2"
                component="h4"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                {group.label}
              </Typography>
              {props.unsyncedGroups?.[group.label] && (
                <UnsyncedChip note={props.unsyncedGroups[group.label]} />
              )}
            </div>
            {group.fields.map(renderRow)}
          </div>
        ))}
      </div>
    </div>
  );
};
